import { useEffect, useState } from 'react'
import { Check, Pencil, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useUpdateInternalOrderCode } from '@/hooks/use-sales-order-query'
import { useAuth } from '@/hooks/use-auth'
import { m } from '@/lib/paraglide/messages'

interface Props {
    orderId: string
    createdById: string
    internalOrderCode?: string | null
}

/**
 * Mã đơn nội bộ — mã đối chiếu với hệ thống ngoài (sàn, đối tác).
 *
 * Sửa được ở MỌI trạng thái đơn (kể cả đã giao xong / đã huỷ) vì mã bên kia có thể
 * được cấp hoặc đổi sau khi đơn đã đi hết vòng đời. Đổi lại, chỉ ĐÚNG NGƯỜI TẠO ĐƠN
 * được sửa — BE trả 403 cho người khác, FE ẩn luôn nút sửa để khỏi mời gọi thao tác
 * chắc chắn thất bại.
 *
 * Luôn render row kể cả khi chưa có mã: nếu ẩn đi thì người tạo đơn không có chỗ nào
 * để điền mã lần đầu.
 */
export function InternalOrderCodeRow({
    orderId,
    createdById,
    internalOrderCode,
}: Props) {
    const { user } = useAuth()
    const update = useUpdateInternalOrderCode()
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(internalOrderCode ?? '')

    const isCreator = !!user?.id && user.id === createdById

    // Đơn được refetch (vd sau khi lưu) → đồng bộ lại draft khi KHÔNG đang sửa.
    // Nếu đồng bộ cả lúc đang sửa thì user đang gõ dở sẽ bị ghi đè.
    useEffect(() => {
        if (editing) return
        setDraft(internalOrderCode ?? '')
    }, [internalOrderCode, editing])

    const save = async () => {
        try {
            await update.mutateAsync({ id: orderId, code: draft.trim() })
            setEditing(false)
        } catch {
            // toast do hook bắn — giữ chế độ sửa để user chỉnh lại và thử tiếp.
        }
    }

    const cancel = () => {
        setDraft(internalOrderCode ?? '')
        setEditing(false)
    }

    return (
        <div className="flex items-start justify-between gap-3 py-1.5">
            <span className="text-sm text-muted-foreground shrink-0">
                {m.order_internal_code_label()}
            </span>

            {editing ? (
                <div className="flex items-center gap-1.5 min-w-0">
                    <Input
                        autoFocus
                        value={draft}
                        maxLength={64}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') void save()
                            if (e.key === 'Escape') cancel()
                        }}
                        placeholder={m.order_internal_code_placeholder()}
                        className="h-8 w-44 font-mono text-xs"
                    />
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-8"
                        aria-label={m.order_internal_code_save()}
                        disabled={update.isPending}
                        onClick={() => void save()}
                    >
                        <Check className="size-4" />
                    </Button>
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-8"
                        aria-label={m.order_internal_code_cancel()}
                        disabled={update.isPending}
                        onClick={cancel}
                    >
                        <X className="size-4" />
                    </Button>
                </div>
            ) : (
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-mono text-sm truncate">
                        {internalOrderCode || (
                            <span className="text-muted-foreground">—</span>
                        )}
                    </span>
                    {isCreator && (
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="size-8"
                            aria-label={m.order_internal_code_edit()}
                            onClick={() => setEditing(true)}
                        >
                            <Pencil className="size-3.5" />
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}
